import { useEffect, useMemo } from 'react';
import {
    createBrowserRouter,
    useLocation,
    RouterProvider,
    Outlet,
    useRouteError,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import {
    ConvertDjangoNotifications,
    NotificationsContainer,
    NotificationsProvider,
} from '../notifications/Notifications';
import { NotFoundPage } from './NotFoundPage';
import { MeasuringPointRoutes, ProjectRoutes } from './measuringPoint/Routes';
import { CustomAlarmPageRoutes } from '../../custom-alarm-page';
import SharingRoutes from './sharing/SharingRoutes';
import DownloadsPage from './downloads/DownloadsPage';
import NotificationsPage from './notifications/NotificationsPage';
import SoundClipsPage from './soundClips/SoundClipsPage';
import { ErrorPage } from './ErrorPage';
import profile, { Profile, ProfileProvider } from '../../profile';
import { Website } from '../website/Website';

// This component updates the URL in the language buttons as soon the
// React router changes URL.
function ChangeLanguageNextUrlUpdater() {
    const { pathname } = useLocation();

    const languageButtons = useMemo(
        () => document.querySelectorAll('.language-menu input[name=next]'),
        []
    );

    useEffect(() => {
        languageButtons.forEach((button) => {
            button.value = pathname;
        });
    }, [pathname, languageButtons]);

    return null;
}

// Used when `react-router-dom` detects an error.
function ReactRouterErrorBoundary() {
    const error = useRouteError();
    return <ErrorPage error={error} />;
}

// Used when our own `ErrorBoundary` wrapped around each route element detects an
// error, or when `react-error-boundary` it's `showBoundary` gets called.
function ErrorFallback({ error }) {
    const { resetBoundary } = useErrorBoundary();

    // An expected error can have a `fallbackElement`. This can be useful, for example,
    // for a `NotFoundError`, which you can raise from deep within the code to display
    // the NotFoundPage.
    const { FallbackElement } = error;
    if (FallbackElement) {
        return <FallbackElement />;
    }
    return <ErrorPage tryAgain={resetBoundary} error={error} />;
}
ErrorFallback.propTypes = {
    error: PropTypes.instanceOf(Error),
};

function OptionalWebsiteWrapper({ wrapWithWebsite, children }) {
    if (wrapWithWebsite) {
        return <Website>{children}</Website>;
    }

    return children;
}
OptionalWebsiteWrapper.propTypes = {
    wrapWithWebsite: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
};

function PageContainer({
    legacyCssContainer,
    createBrowserRouterMethod = createBrowserRouter,
    routerProps,
    extraRoutes = [],
    routerInjectables = [],
    profile: profileOverride,
    wrapWithWebsite = false,
}) {
    const router = useMemo(
        () =>
            createBrowserRouterMethod(
                [
                    {
                        errorElement: <ReactRouterErrorBoundary />,
                        element: (
                            <OptionalWebsiteWrapper wrapWithWebsite={wrapWithWebsite}>
                                <ErrorBoundary FallbackComponent={ErrorFallback}>
                                    <ChangeLanguageNextUrlUpdater />
                                    {routerInjectables.map((injectable) => injectable)}
                                    <Outlet />
                                </ErrorBoundary>
                            </OptionalWebsiteWrapper>
                        ),
                        children: [
                            { path: '/measuring_point/*', element: <MeasuringPointRoutes /> },
                            { path: '/project/*', element: <ProjectRoutes /> },
                            { path: '/alarm_level/*', element: <CustomAlarmPageRoutes /> },
                            { path: '/sharing/*', element: <SharingRoutes /> },
                            { path: '/reports/downloads', element: <DownloadsPage /> },
                            { path: '/notifications', element: <NotificationsPage /> },
                            {
                                path: '/sound_clips/:id/:startTime/:endTime/',
                                element: <SoundClipsPage />,
                            },
                            ...extraRoutes.map((route) => route),
                            { path: '*', element: <NotFoundPage /> },
                        ],
                    },
                ],
                routerProps
            ),
        [createBrowserRouterMethod, extraRoutes, routerProps, routerInjectables, wrapWithWebsite]
    );

    return (
        <ProfileProvider profile={profileOverride || profile}>
            <NotificationsProvider>
                <ConvertDjangoNotifications />
                <div className={legacyCssContainer ? '' : 'main-container-new'}>
                    <NotificationsContainer />
                </div>

                <RouterProvider router={router} />
            </NotificationsProvider>
        </ProfileProvider>
    );
}
PageContainer.propTypes = {
    legacyCssContainer: PropTypes.bool,
    createBrowserRouterMethod: PropTypes.func,
    routerProps: PropTypes.object,
    extraRoutes: PropTypes.arrayOf(PropTypes.object),
    routerInjectables: PropTypes.arrayOf(PropTypes.object),
    profile: PropTypes.instanceOf(Profile),
    wrapWithWebsite: PropTypes.bool,
};

export default PageContainer;
